import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

import LoadingComponent from "../../shared_modules/loadingComponent";
import { useApi } from "../../hooks/useApi";
import { formatMonthYear, formatPhoneNumber } from "../../utils/functions";

const fmt = (n) =>
  Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });

const fmtDate = (utcStr) => {
  if (!utcStr) return "—";
  return new Date(utcStr.replace(" ", "T") + "Z").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const fmtApptDate = (localStr) => {
  if (!localStr) return "—";
  return new Date(localStr.replace(" ", "T")).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const fmtApptTime = (localStr) => {
  if (!localStr) return "—";
  return new Date(localStr.replace(" ", "T")).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const STATUS_STYLE = {
  completed: { bg: "#e8f7f2", color: "#1D9E75", label: "Completed" },
  confirmed: { bg: "#e8f1fb", color: "#378ADD", label: "Confirmed" },
  cancelled: { bg: "#f5f5f5", color: "#777", label: "Cancelled" },
  pending: { bg: "#f0effc", color: "#7F77DD", label: "Pending" },
  no_show: { bg: "#fef4e8", color: "#EF9F27", label: "No Show" },
};

// ── Pagination ────────────────────────────────────────────────────────────────
function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
  const totalPages = Math.ceil(count / rowsPerPage);
  return (
    <Box sx={{ display: "flex", gap: 0.5, ml: 2, mr: 2 }}>
      {Array.from({ length: totalPages }, (_, i) => (
        <IconButton
          key={i}
          onClick={(e) => onPageChange(e, i)}
          sx={{
            width: 32,
            height: 32,
            borderRadius: "6px",
            backgroundColor: page === i ? "primary.main" : "transparent",
            color: page === i ? "white" : "text.primary",
            fontSize: "0.875rem",
            "@media (hover: hover)": {
              "&:hover": {
                backgroundColor: page === i ? "primary.dark" : "grey.100",
              },
            },
          }}
        >
          {i + 1}
        </IconButton>
      ))}
    </Box>
  );
}

// ── Client Drawer ─────────────────────────────────────────────────────────────
const ClientDrawer = ({ client, open, onClose }) => {
  const api = useApi();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!client || !open) return;
    setLoading(true);
    setBookings([]);
    api(`/tenants/appointments.php?client_id=${client.user_id}`, {
      method: "GET",
    })
      .then((res) => setBookings(res.data.bookings || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [client, open]);

  if (!client) return null;

  const avgVisit =
    client.visit_count > 0 ? client.total_spent / client.visit_count : 0;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: { xs: "100vw", sm: 500 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.default",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2.5,
            borderBottom: "0.1px solid",
            borderColor: "customDivider",
            backgroundColor: "offWhite",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Avatar
                src={client.avatar_url || undefined}
                sx={{
                  width: 48,
                  height: 48,
                  fontSize: 16,
                  fontWeight: 600,
                  backgroundColor: "grey.200",
                  color: "text.primary",
                  flexShrink: 0,
                }}
              >
                {!client.avatar_url &&
                  `${client.first_name?.[0] ?? ""}${client.last_name?.[0] ?? ""}`}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>
                  {client.first_name} {client.last_name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {client.email}
                </Typography>
                {client.phone_number && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <a
                      href={`tel:${client.phone_number}`}
                      style={{ color: "inherit", textDecoration: "none" }}
                    >
                      {formatPhoneNumber(client.phone_number)}
                    </a>
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{ color: "text.disabled", mt: 0.5, display: "block" }}
                >
                  Member since {formatMonthYear(client.joined_at)}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Stats */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
            gap: 1,
            px: 3,
            py: 2,
            borderBottom: "0.1px solid",
            borderColor: "customDivider",
            backgroundColor: "offWhite",
          }}
        >
          {[
            { label: "Visits", value: client.visit_count },
            { label: "Total Spent", value: fmt(client.total_spent) },
            { label: "Avg / Visit", value: fmt(avgVisit) },
            {
              label: "Last Visit",
              value: client.last_visit ? fmtDate(client.last_visit) : "—",
            },
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{
                textAlign: "center",
                p: 1.5,
                borderRadius: 2,
                border: "0.1px solid",
                borderColor: "customDivider",
                backgroundColor: "background.default",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, fontSize: 13 }}
              >
                {value}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: 10 }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Booking history */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
            Booking History
          </Typography>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
              <CircularProgress size={22} sx={{ color: "text.secondary" }} />
            </Box>
          ) : bookings.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No bookings found for this client.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {bookings.map((appt) => {
                const s = STATUS_STYLE[appt.status] || STATUS_STYLE.pending;
                return (
                  <Box
                    key={appt.id}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "0.1px solid",
                      borderColor: "customDivider",
                      backgroundColor: "offWhite",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 0.25 }}
                        >
                          {appt.service?.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled" }}
                        >
                          {fmtApptDate(appt.starts_at_local)} &middot;{" "}
                          {fmtApptTime(appt.starts_at_local)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700 }}
                        >
                          {fmt(appt.price_charged)}
                        </Typography>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            backgroundColor: s.bg,
                            mt: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: s.color,
                              lineHeight: 1.4,
                            }}
                          >
                            {s.label}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

// ── Sortable column header ─────────────────────────────────────────────────────
const SortHeader = ({
  label,
  sortKey,
  activeSortKey,
  activeSortDir,
  onSort,
  align = "left",
}) => (
  <TableCell align={align}>
    <Box
      onClick={() => onSort(sortKey)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        cursor: "pointer",
        userSelect: "none",
        color: activeSortKey === sortKey ? "text.primary" : "text.secondary",
        fontWeight: activeSortKey === sortKey ? 700 : 600,
        fontSize: "0.8125rem",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        width: "100%",
      }}
    >
      {label}
      {activeSortKey === sortKey ? (
        activeSortDir === "asc" ? (
          <ArrowUpwardIcon sx={{ fontSize: 13 }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 13 }} />
        )
      ) : (
        <UnfoldMoreIcon sx={{ fontSize: 13, opacity: 0.35 }} />
      )}
    </Box>
  </TableCell>
);

// ── Main component ─────────────────────────────────────────────────────────────
const BarberClients = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const api = useApi();
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [drawerClient, setDrawerClient] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    api("/tenants/clients.php", { method: "GET" })
      .then((res) => setClients(res.data.clients || []))
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
    setPage(0);
  };

  const filteredClients = clients.filter((c) => {
    const query = search.toLowerCase().trim();
    const matchesText = `${c.first_name} ${c.last_name} ${c.email}`
      .toLowerCase()
      .includes(query);
    const queryDigits = query.replace(/\D/g, "");
    const phoneDigits = (c.phone_number || "").replace(/\D/g, "");
    const matchesPhone =
      queryDigits.length >= 3 && phoneDigits.includes(queryDigits);
    return matchesText || matchesPhone;
  });

  const sortedClients = [...filteredClients].sort((a, b) => {
    let aVal =
      sortKey === "name"
        ? `${a.first_name} ${a.last_name}`.toLowerCase()
        : a[sortKey];
    let bVal =
      sortKey === "name"
        ? `${b.first_name} ${b.last_name}`.toLowerCase()
        : b[sortKey];
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const paginatedClients =
    rowsPerPage > 0
      ? sortedClients.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage,
        )
      : sortedClients;

  return (
    <Box
      sx={{ maxWidth: 1100, mx: "auto", px: { xs: 3, md: 5 }, pt: 5, pb: 10 }}
    >
      <Typography variant={isMobile ? "h4" : "h3"}>Clients</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
        Clients who have booked with you.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <LoadingComponent marginTop={10} />
      ) : (
        <>
          <Box sx={{ mb: 2, width: { xs: "100%", md: 500 } }}>
            <TextField
              variant="outlined"
              fullWidth
              placeholder="Search by name, email or phone..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "offWhite",
                },
              }}
            />
          </Box>

          {clients.length > 0 ? (
            <TableContainer
              sx={{
                mb: 10,
                border: "0.1px solid",
                borderColor: "customDivider",
                borderRadius: 2,
                overflowX: "auto",
                backgroundColor: "offWhite",
              }}
            >
              <Table size="medium" sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <SortHeader
                      label="Client"
                      sortKey="name"
                      activeSortKey={sortKey}
                      activeSortDir={sortDir}
                      onSort={handleSort}
                    />
                    <TableCell>
                      <Typography variant="subtitle2">Phone</Typography>
                    </TableCell>
                    <SortHeader
                      label="Visits"
                      sortKey="visit_count"
                      activeSortKey={sortKey}
                      activeSortDir={sortDir}
                      onSort={handleSort}
                      align="center"
                    />
                    <SortHeader
                      label="Total Spent"
                      sortKey="total_spent"
                      activeSortKey={sortKey}
                      activeSortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Last Visit"
                      sortKey="last_visit"
                      activeSortKey={sortKey}
                      activeSortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Member Since"
                      sortKey="joined_at"
                      activeSortKey={sortKey}
                      activeSortDir={sortDir}
                      onSort={handleSort}
                    />
                    <TableCell align="right">
                      <Typography variant="subtitle2">History</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedClients.map((client) => (
                    <TableRow
                      key={client.user_id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                          }}
                        >
                          <Avatar
                            src={client.avatar_url || undefined}
                            sx={{
                              width: 34,
                              height: 34,
                              fontSize: 12,
                              fontWeight: 600,
                              backgroundColor: "grey.200",
                              color: "text.primary",
                              flexShrink: 0,
                            }}
                          >
                            {!client.avatar_url &&
                              `${client.first_name?.[0] ?? ""}${client.last_name?.[0] ?? ""}`}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {client.first_name} {client.last_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              <a
                                href={`mailto:${client.email}`}
                                style={{
                                  color: "inherit",
                                  textDecoration: "none",
                                }}
                              >
                                {client.email}
                              </a>
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          <a
                            href={`tel:${client.phone_number || ""}`}
                            style={{
                              color: "inherit",
                              textDecoration: "none",
                            }}
                          >
                            {formatPhoneNumber(client.phone_number || "")}
                          </a>
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="body2">
                          {client.visit_count}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {fmt(client.total_spent)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {client.last_visit ? fmtDate(client.last_visit) : "—"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {formatMonthYear(client.joined_at)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <IconButton
                          sx={{ color: "text.primary" }}
                          onClick={() => setDrawerClient(client)}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "customDivider",
                    display:
                      filteredClients.length > rowsPerPage
                        ? "table-footer-group"
                        : "none",
                  }}
                >
                  <TableRow sx={{ borderBottom: "none" }}>
                    <TablePagination
                      sx={{ borderBottom: "none" }}
                      rowsPerPageOptions={[
                        5,
                        10,
                        25,
                        { label: "All", value: -1 },
                      ]}
                      colSpan={7}
                      count={filteredClients.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      slotProps={{
                        select: {
                          inputProps: { "aria-label": "rows per page" },
                          native: true,
                        },
                      }}
                      onPageChange={(_, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 300 }}
            >
              No clients yet. Clients who book with you will appear here.
            </Typography>
          )}
        </>
      )}

      <ClientDrawer
        client={drawerClient}
        open={Boolean(drawerClient)}
        onClose={() => setDrawerClient(null)}
      />
    </Box>
  );
};

export default BarberClients;
